import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DemandRequestStatus } from '../../../graphql'
import { cleanFulfillmentOrderNumber, parseFulfillmentOrderNumber } from '../utils/fulfillmentOrderNumber'

export const DEFAULT_ROW_SIZE = 20
export const ROW_SIZE_OPTIONS = [ 10, 20, 50 ]

export type RequestsListPaging = {
	first?: number,
	last?: number,
	after?: string,
	before?: string,
}

export const PARAMS = {
	status: 'status',
	assignedTo: 'assignedTo',
	requestedBy: 'requestedBy',
	requestNumber: 'req',
	fulfillmentOrderNumber: 'fo',
	partId: 'partId',
	rowSize: 'size',
	after: 'after',
	before: 'before',
} as const

/** A cursor only has meaning within one result set, so editing any of these invalidates it. */
const FILTER_PARAMS = [
	PARAMS.status,
	PARAMS.assignedTo,
	PARAMS.requestedBy,
	PARAMS.requestNumber,
	PARAMS.fulfillmentOrderNumber,
	PARAMS.partId,
]

/** Order is a UI detail, so compare selections by content rather than by array identity. */
function storeSelectionKey( inventoryLocationIds?: string[] ): string {
	return [ ...( inventoryLocationIds ?? [] ) ].sort().join( ',' )
}

const VALID_STATUSES = new Set<string>( Object.values( DemandRequestStatus ) )

function parsePositiveInt( raw: string | null ): number | undefined {
	if ( !raw ) {
		return undefined
	}
	const parsed = parseInt( raw, 10 )
	return Number.isNaN( parsed ) || parsed < 0 ? undefined : parsed
}

/**
 * List state for the Requests view, held in the URL query string.
 *
 * `/requests` and `/requests/:id` are sibling routes, so opening a request
 * unmounts the list. Component state cannot survive that — which is why
 * opening a request used to drop the user back at page one with their filters
 * cleared. Keeping the state in the URL means the browser restores it for
 * free on back-navigation, and makes a filtered list shareable.
 *
 * Writes use `replace`, so the list occupies a single history entry that always
 * reflects the latest state. Backing out of a request detail therefore lands on
 * the list as it was last seen, instead of unwinding one filter edit at a time.
 *
 * Store selection is app-wide state shared with the other inventory views, so it
 * stays in its reactive variable rather than moving into this URL. It still
 * filters the result set though, so it is passed in here: everything that
 * decides which requests come back has to live behind one notion of "the
 * filters", or a cursor outlives the result set it was minted against.
 */
export default function useRequestsListParams( inventoryLocationIds?: string[], defaultStoresApplied = false ) {
	const [ searchParams, setSearchParams ] = useSearchParams()

	// Unknown status values are dropped rather than forwarded to the API, so a
	// hand-edited or stale URL cannot put garbage in the query variables.
	const statuses = useMemo(
		() => searchParams
			.getAll( PARAMS.status )
			.filter( ( value ): value is DemandRequestStatus => VALID_STATUSES.has( value ) ),
		[ searchParams ]
	)

	const assignedToIds = useMemo( () => searchParams.getAll( PARAMS.assignedTo ), [ searchParams ] )
	const requestedByIds = useMemo( () => searchParams.getAll( PARAMS.requestedBy ), [ searchParams ] )

	/** Raw text so the search box can be seeded with exactly what the user typed. */
	const requestNumberInput = searchParams.get( PARAMS.requestNumber ) ?? ''
	const fulfillmentOrderNumberInput = searchParams.get( PARAMS.fulfillmentOrderNumber ) ?? ''

	const fulfillmentOrderNumbers = useMemo( () => {
		const parsed = parseFulfillmentOrderNumber( fulfillmentOrderNumberInput )
		return parsed === undefined ? undefined : [ parsed ]
	}, [ fulfillmentOrderNumberInput ] )

	const requestNumbers = useMemo( () => {
		const parsed = parsePositiveInt( requestNumberInput )
		return parsed === undefined ? undefined : [ parsed ]
	}, [ requestNumberInput ] )

	const partId = searchParams.get( PARAMS.partId ) ?? undefined

	const rowSize = useMemo( () => {
		const parsed = parsePositiveInt( searchParams.get( PARAMS.rowSize ) )
		return parsed !== undefined && ROW_SIZE_OPTIONS.includes( parsed ) ? parsed : DEFAULT_ROW_SIZE
	}, [ searchParams ] )

	const paging = useMemo<RequestsListPaging>( () => {
		const after = searchParams.get( PARAMS.after )
		if ( after ) {
			return { first: rowSize, after }
		}
		const before = searchParams.get( PARAMS.before )
		if ( before ) {
			return { last: rowSize, before }
		}
		return { first: rowSize }
	}, [ searchParams, rowSize ] )

	const isFirstPage = !searchParams.get( PARAMS.after ) && !searchParams.get( PARAMS.before )

	// react-router 6.3 has no functional updater for setSearchParams, so the next
	// params are built from the current render's value.
	const commit = useCallback( ( mutate: ( next: URLSearchParams ) => void ) => {
		const next = new URLSearchParams( searchParams )
		mutate( next )
		setSearchParams( next, { replace: true } )
	}, [ searchParams, setSearchParams ] )

	const clearCursor = ( params: URLSearchParams ) => {
		params.delete( PARAMS.after )
		params.delete( PARAMS.before )
	}

	const setFilter = useCallback( ( key: string, values: string[] ) => {
		commit( ( next ) => {
			next.delete( key )
			values.forEach( ( value ) => next.append( key, value ) )
			clearCursor( next )
		} )
	}, [ commit ] )

	const setStatuses = useCallback(
		( next: DemandRequestStatus[] ) => setFilter( PARAMS.status, next ),
		[ setFilter ]
	)

	const setAssignedToIds = useCallback(
		( next: string[] ) => setFilter( PARAMS.assignedTo, next ),
		[ setFilter ]
	)

	const setRequestedByIds = useCallback(
		( next: string[] ) => setFilter( PARAMS.requestedBy, next ),
		[ setFilter ]
	)

	const setRequestNumberInput = useCallback(
		( next: string ) => setFilter( PARAMS.requestNumber, next.trim() ? [ next.trim() ] : [] ),
		[ setFilter ]
	)

	const setPartId = useCallback(
		( next?: string ) => setFilter( PARAMS.partId, next ? [ next ] : [] ),
		[ setFilter ]
	)

	const setFulfillmentOrderNumberInput = useCallback( ( next: string ) => {
		const cleaned = cleanFulfillmentOrderNumber( next )
		setFilter( PARAMS.fulfillmentOrderNumber, cleaned ? [ cleaned ] : [] )
	}, [ setFilter ] )

	const setRowSize = useCallback( ( next: number ) => {
		commit( ( params ) => {
			params.set( PARAMS.rowSize, String( next ) )
			// Row size changes the page boundaries, so the old cursor no longer lines up.
			clearCursor( params )
		} )
	}, [ commit ] )

	const pageForward = useCallback( ( cursor: string ) => () => {
		commit( ( params ) => {
			params.delete( PARAMS.before )
			params.set( PARAMS.after, cursor )
		} )
	}, [ commit ] )

	const pageBackward = useCallback( ( cursor: string ) => () => {
		commit( ( params ) => {
			params.delete( PARAMS.after )
			params.set( PARAMS.before, cursor )
		} )
	}, [ commit ] )

	const resetPaging = useCallback( () => commit( clearCursor ), [ commit ] )

	// Changing store changes the result set, so the cursor has to go with it — the
	// same reason editing a query-string filter clears it.
	//
	// The baseline waits on `defaultStoresApplied`: until the default store lands, a
	// cold load changes selection on its own and would strip a shared link's cursor.
	const storeKey = storeSelectionKey( inventoryLocationIds )
	const baselineStoreKey = useRef<string | null>( null )

	useEffect( () => {
		if ( !defaultStoresApplied ) {
			return
		}
		if ( baselineStoreKey.current === null ) {
			baselineStoreKey.current = storeKey
			return
		}
		if ( baselineStoreKey.current === storeKey ) {
			return
		}
		baselineStoreKey.current = storeKey
		resetPaging()
	}, [ defaultStoresApplied, storeKey, resetPaging ] )

	const hasAnyFilter = FILTER_PARAMS.some( ( param ) => searchParams.getAll( param ).length > 0 )
		|| storeKey !== ''

	return {
		statuses,
		assignedToIds,
		requestedByIds,
		requestNumberInput,
		requestNumbers,
		fulfillmentOrderNumberInput,
		fulfillmentOrderNumbers,
		partId,
		rowSize,
		paging,
		isFirstPage,
		hasAnyFilter,
		setStatuses,
		setAssignedToIds,
		setRequestedByIds,
		setRequestNumberInput,
		setFulfillmentOrderNumberInput,
		setPartId,
		setRowSize,
		pageForward,
		pageBackward,
		resetPaging,
	}
}
